import { memo, useReducer } from 'react';

import { SquarePen, Trash2 } from 'lucide-react';
import { omit } from 'remeda';
import { toast } from 'sonner';
import useSWRMutation from 'swr/mutation';

import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';

import { CasinoFormDialog } from '../../casinoFormDialog/CasinoFormDialog';
import { CasinoForm } from '../../casinoFormDialog/form';

import css from './CasinoCard.module.css';

import { casinoService } from 'v2/api/casinoService';
import { Popconfirm } from 'v2/components/popconfirm/Popconfirm';
import { CasinoModel } from 'v2/types/CasinoModel';
import { fileSrc, handleHttpError } from 'v2/utils';

interface CasinoCardProps {
  casino: CasinoModel;
  onRevalidate: VoidFunction;
}

export const CasinoCard = memo<CasinoCardProps>(({ casino, onRevalidate }) => {
  const [isOpen, setOpen] = useReducer((state) => !state, false);

  const { trigger: triggerUpdate, isMutating } = useSWRMutation(
    ['updateCasino', casino.id],
    casinoService.updateCasino,
    {
      onSuccess: () => {
        onRevalidate();
        toast.success('Casino has been successfully updated');
        setOpen();
      },
      onError: handleHttpError,
    }
  );

  const { trigger: triggerDelete } = useSWRMutation(['deleteCasino', casino.id], casinoService.deleteCasino, {
    onSuccess: () => {
      onRevalidate();
    },
    onError: handleHttpError,
  });

  const { trigger: triggerActive } = useSWRMutation(['setActiveCasino', casino.id], casinoService.setActiveCasino, {
    onSuccess: () => {
      onRevalidate();
    },
    onError: handleHttpError,
  });

  const handleToggleActive = () => {
    triggerActive(!casino.isActive);
  };

  const handleSubmit = (values: CasinoForm, resetForm: VoidFunction) => {
    triggerUpdate(values).then(() => {
      resetForm();
    });
  };

  return (
    <div className={css.casinoCard}>
      <div style={{ backgroundImage: `url("${fileSrc(casino.solidLogoId)}")` }} className={css.casinoCardPreview} />

      <div className={css.casinoCardContent}>
        <div className={css.casinoCardContentInner}>
          <div className={css.casinoCardContentTitle}>
            <p className={css.casinoCardName}>{casino.name}</p>

            <Chip
              onClick={handleToggleActive}
              color={casino.isActive ? 'success' : 'default'}
              label={casino.isActive ? 'Active' : 'Inactive'}
              size="small"
            />
          </div>
        </div>

        <div className={css.casinoCardActions}>
          <Popconfirm message="Are you sure that you want to delete this casino?" onConfirm={triggerDelete}>
            <Button startIcon={<Trash2 width={18} />} variant="outlined" color="error" fullWidth>
              Delete
            </Button>
          </Popconfirm>

          <Button onClick={setOpen} startIcon={<SquarePen width={18} />} variant="outlined" color="warning">
            Edit
          </Button>

          <CasinoFormDialog
            title="Edit Casino"
            isOpen={isOpen}
            onClose={setOpen}
            initialValues={omit(casino, ['id', 'order'])}
            isSubmitting={isMutating}
            onSubmit={handleSubmit}
          />
        </div>
      </div>
    </div>
  );
});

CasinoCard.displayName = 'CasinoCard';
